/**
 * Guarda de permiso de la pantalla del Cotizador de puertas.
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla (mismo
 * patrón que `cutlist/RequireCutlistPermission` y `scheduling/RequireSchedulePermission`).
 * Replica la paridad con el SSR (`docs/frontend_architecture.md`, Pr. 5): el cotizador exige
 * `use_cotizador` (mismo permiso que el menú del SSR, la página y la API); sin él se muestra
 * un aviso en vez del contenido.
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { COTIZADOR_PERMISSION } from "../constants";

interface RequireCotizadorPermissionProps {
  children: ReactNode;
}

export function RequireCotizadorPermission({
  children,
}: RequireCotizadorPermissionProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los permisos:
  // mostrar "sin permiso" en ese instante sería engañoso. Se espera a tener la sesión
  // resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cotizador de puertas</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(COTIZADOR_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cotizador de puertas</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para usar el cotizador de puertas.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
